import { AnalyticsPlatforms, useAnalytics } from '@dni-analysis'
import { LOGIN_TYPE } from '@dni-analysis/types/cosign-events'
import { useCallback } from 'react'
import { type ThirtyPartyLoginType } from '../types/dataModel/platformSdk'
import { wordPressHost } from '../utils/location'
import { useRequestLoginByFacebook } from './apis/wp/useRequestLoginByFacebook'
import { useRequestLoginByGoogle } from './apis/wp/useRequestLoginByGoogle'
import { useRequestLoginByLine } from './apis/wp/useRequestLoginByLine'

interface UseThirtyPartyBindIdentityProps {
  onError?: () => void
}

export function useThirtyPartyBindIdentity(props?: UseThirtyPartyBindIdentityProps) {
  const { trigger: loginByLine, isMutating: isMutatingLoginByLine } = useRequestLoginByLine()
  const { trigger: loginByFacebook, isMutating: isMutatingLoginByFacebook } = useRequestLoginByFacebook()
  const { trigger: loginByGoogle, isMutating: isMutatingLoginByGoogle } = useRequestLoginByGoogle()
  const isMutating = isMutatingLoginByLine || isMutatingLoginByFacebook || isMutatingLoginByGoogle
  const { sendEvent } = useAnalytics({ defaultPlatforms: [AnalyticsPlatforms.GA4] })

  const sendGa4BindThirtyParty = useCallback((type: ThirtyPartyLoginType) => {
    sendEvent({
      eventName: 'login method',
      eventParams: {
        event_category: 'Login',
        account_type: type,
      },
    })
  }, [sendEvent])

  const bindThirtyParty = useCallback(async (type: ThirtyPartyLoginType, redirectPath = '/my-account/edit-account/') => {
    try {
      if (type === LOGIN_TYPE.LINE) {
        const { data } = await loginByLine({
          redirectUrl: `${wordPressHost}${redirectPath}?lineBinding=true`,
          fallbackUrl: `${wordPressHost}${redirectPath}?lineBinding=false`,
        })
        sendGa4BindThirtyParty(type)
        window.location.href = data.data.url
      }
      if (type === LOGIN_TYPE.FACEBOOK) {
        const { data } = await loginByFacebook({
          redirectUrl: `${wordPressHost}${redirectPath}?facebookBinding=true`,
          fallbackUrl: `${wordPressHost}${redirectPath}?facebookBinding=false`,
        })
        sendGa4BindThirtyParty(type)
        window.location.href = data.data.url
      }
      if (type === LOGIN_TYPE.GOOGLE) {
        const { data } = await loginByGoogle({
          redirectUrl: `${wordPressHost}${redirectPath}?googleBinding=true`,
          fallbackUrl: `${wordPressHost}${redirectPath}?googleBinding=false`,
        })
        sendGa4BindThirtyParty(type)
        window.location.href = data.data.url
      }
    }
    catch (e) {
      sendEvent({
        eventName: 'login failed',
        eventParams: {
          event_category: 'Login',
          login_error: 'thirty party login error',
        },
      })
      props?.onError?.()
      console.error(e)
    }
  }, [loginByFacebook, loginByGoogle, loginByLine, props, sendEvent, sendGa4BindThirtyParty])

  const bindLine = useCallback(() => bindThirtyParty(LOGIN_TYPE.LINE), [bindThirtyParty])
  const bindFacebook = useCallback(() => bindThirtyParty(LOGIN_TYPE.FACEBOOK), [bindThirtyParty])
  const bindGoogle = useCallback(() => bindThirtyParty(LOGIN_TYPE.GOOGLE), [bindThirtyParty])

  return {
    bindLine,
    bindFacebook,
    bindGoogle,
    isMutating,
  }
}
